import { RequestParameters, Variables, GraphQLResponse } from 'relay-runtime';
import { isTauriEnvironment } from '@daysheetly/desktop-bridge';
import { getRuntimeAuthSession } from '@/features/auth/authRuntime';

const ACTIVE_WORKSPACE_KEY = 'daysheetly_active_workspace_id';
const TOKEN_KEY = 'daysheetly_token';
/**
 * Dispatched on `window` when a GraphQL response signals that the local
 * token is no longer valid (HTTP 401, a `Not authenticated` GraphQL error,
 * or a top-level `viewer: null` while a token is present). `AuthContext`
 * listens for this and clears local storage so `<ProtectedRoute>` can
 * redirect to `/login`.
 */
export const AUTH_LOGOUT_EVENT = 'daysheetly:auth-logout';

function signalAuthLogout() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(AUTH_LOGOUT_EVENT));
}

interface GraphQLResponseBody {
  data?: Record<string, unknown> | null;
  errors?: ReadonlyArray<{ message?: string }>;
}

function isAuthFailure(json: GraphQLResponseBody, hasToken: boolean): boolean {
  if (!hasToken) return false;
  // 1. GraphQL error matching the server's `throw new Error('Not authenticated')`.
  if (Array.isArray(json.errors)) {
    const matched = json.errors.some(
      (e) => typeof e?.message === 'string' && /not authenticated/i.test(e.message),
    );
    if (matched) return true;
  }
  // 2. Top-level `viewer: null` while we *thought* we were authenticated.
  //    This catches stale tokens that the server silently treats as
  //    anonymous (the request still succeeds, just without a viewer).
  if (json.data && Object.prototype.hasOwnProperty.call(json.data, 'viewer')) {
    if (json.data.viewer === null || json.data.viewer === undefined) {
      return true;
    }
  }
  return false;
}

export async function fetchGraphQL(
  request: RequestParameters,
  variables: Variables
): Promise<GraphQLResponse> {
  const endpoint = process.env.GRAPHQL_ENDPOINT || '/graphql';
  const runtimeSession = getRuntimeAuthSession();
  const native = isTauriEnvironment();
  const token = runtimeSession.token ?? (native ? null : localStorage.getItem(TOKEN_KEY));
  // The active workspace id is sent on every request so the server can
  // scope Project / Task queries server-side. When unset, the server
  // falls back to user.preferences.activeWorkspaceId, then to no scope.
  const activeWorkspaceId =
    runtimeSession.activeWorkspaceId ??
    (native ? null : localStorage.getItem(ACTIVE_WORKSPACE_KEY));

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (activeWorkspaceId) headers['x-workspace-id'] = activeWorkspaceId;

  const response = await fetch(endpoint, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      query: request.text,
      variables,
    }),
  });

  if (response.status === 401 || response.status === 403) {
    signalAuthLogout();
    throw new Error('Authentication required');
  }

  const json = (await response.json()) as GraphQLResponseBody;

  if (isAuthFailure(json, !!token)) {
    signalAuthLogout();
  }

  if (!response.ok) {
    throw new Error(`GraphQL request failed: ${response.statusText}`);
  }

  return json as GraphQLResponse;
}
